/**
 * Shape mask layer for the mesh gradient renderer.
 *
 * Two modes:
 *  - "mask":      mesh renders only inside the shape (lovable.dev heart-glow look)
 *  - "silhouette" mesh stays full-bleed; the shape sits on top as a flat-color cutout
 *  - "vignette":  inverse — outside the shape darkens to focus attention inside
 *
 * Shape source is either a built-in preset path or a sanitized custom SVG string,
 * stored inline in the mesh config (data-URI in CSS mask-image).
 */

export type ShapeMaskMode = "mask" | "silhouette" | "vignette";

export type ShapeMaskPreset =
  | "heart"
  | "circle"
  | "blob"
  | "leaf"
  | "diamond"
  | "hexagon"
  | "arch"
  | "custom";

export type ShapeMaskHalo = {
  enabled: boolean;
  /** halo scale relative to main shape, 1.0–2.5 */
  size: number;
  /** halo opacity 0–1 */
  intensity: number;
  /** halo fill color (hex) */
  color: string;
};

export type ShapeMaskExtendOutside = {
  /** Render an unmasked copy of the mesh behind the shape so the area outside isn't flat base color. Mask mode only. */
  enabled: boolean;
  /** Opacity of the outside mesh layer, 0–1 */
  intensity: number;
};

export type ShapeMaskConfig = {
  enabled: boolean;
  mode: ShapeMaskMode;
  preset: ShapeMaskPreset;
  /** Sanitized SVG markup, only used when preset === "custom". */
  customSvg?: string;
  /** % position of shape center within the viewport */
  x: number; // 0–100
  y: number; // 0–100
  /** % size relative to min(viewport-w, viewport-h) */
  size: number; // 10–200
  /** rotation in degrees */
  rotation: number;
  /** feather radius in px (gaussian blur on mask edges) */
  feather: number; // 0–80
  /** strength 0–1 (for vignette/silhouette opacity) */
  intensity: number;
  halo: ShapeMaskHalo;
  extendOutside: ShapeMaskExtendOutside;
};

export const DEFAULT_SHAPE_MASK: ShapeMaskConfig = {
  enabled: false,
  mode: "mask",
  preset: "heart",
  x: 50,
  y: 50,
  size: 70,
  rotation: 0,
  feather: 24,
  intensity: 1,
  halo: { enabled: false, size: 1.35, intensity: 0.5, color: "#000000" },
  extendOutside: { enabled: false, intensity: 0.35 },
};


/** Built-in SVG path data, all designed in a 100×100 viewBox, centered. */
const PRESET_PATHS: Record<Exclude<ShapeMaskPreset, "custom">, string> = {
  // Classic heart, centered, fills the viewBox.
  heart:
    "M50,86 C20,66 6,48 6,30 C6,16 17,6 30,6 C39,6 46,11 50,18 C54,11 61,6 70,6 C83,6 94,16 94,30 C94,48 80,66 50,86 Z",
  circle: "M50,50 m-44,0 a44,44 0 1,0 88,0 a44,44 0 1,0 -88,0 Z",
  // Soft organic blob (single closed bezier).
  blob:
    "M52,8 C70,6 88,18 92,38 C96,58 84,76 66,86 C48,96 26,92 14,76 C2,60 4,38 16,24 C26,12 38,9 52,8 Z",
  leaf:
    "M50,6 C78,18 92,40 86,68 C82,86 66,94 50,94 C34,94 18,86 14,68 C8,40 22,18 50,6 Z",
  diamond: "M50,4 L92,50 L50,96 L8,50 Z",
  hexagon: "M50,6 L88,28 L88,72 L50,94 L12,72 L12,28 Z",
  // Tall rounded arch.
  arch:
    "M14,94 L14,50 C14,26 30,10 50,10 C70,10 86,26 86,50 L86,94 Z",
};

/** Sanitize an uploaded SVG: strip <script>, on*-handlers, external refs, and DOCTYPE. */
export const sanitizeSvg = (raw: string): string => {
  let s = raw;
  // strip XML / DOCTYPE prologue
  s = s.replace(/<\?xml[\s\S]*?\?>/gi, "");
  s = s.replace(/<!DOCTYPE[\s\S]*?>/gi, "");
  // remove script/style/foreignObject blocks
  s = s.replace(/<script[\s\S]*?<\/script>/gi, "");
  s = s.replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, "");
  // remove on*="..." event handlers
  s = s.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "");
  s = s.replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "");
  // neutralize external/javascript hrefs
  s = s.replace(/\s(href|xlink:href)\s*=\s*"(?!#)[^"]*"/gi, "");
  s = s.replace(/\s(href|xlink:href)\s*=\s*'(?!#)[^']*'/gi, "");
  return s.trim();
};

/** Best-effort viewBox extraction; falls back to "0 0 100 100". */
const extractViewBox = (svg: string): string => {
  const m = svg.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (m) return m[1];
  const w = svg.match(/\swidth\s*=\s*["']?([\d.]+)/i);
  const h = svg.match(/\sheight\s*=\s*["']?([\d.]+)/i);
  if (w && h) return `0 0 ${w[1]} ${h[1]}`;
  return "0 0 100 100";
};

/** Strip the outer <svg ...> wrapper, returning the inner markup. */
const innerSvg = (svg: string): string => {
  const m = svg.match(/<svg[^>]*>([\s\S]*)<\/svg>/i);
  return m ? m[1] : svg;
};

/** Extract shape body normalized to a 100×100 viewBox (returns inner markup + the source viewBox). */
const shapeBody = (
  shape: ShapeMaskConfig,
  fill: string,
): { inner: string; vx: number; vy: number; vw: number; vh: number } => {
  if (shape.preset === "custom" && shape.customSvg) {
    const viewBox = extractViewBox(shape.customSvg);
    const [vx, vy, vw, vh] = viewBox.split(/\s+/).map(Number);
    let inner = innerSvg(shape.customSvg);
    inner = inner.replace(/\sfill\s*=\s*"[^"]*"/gi, "");
    inner = inner.replace(/\sfill\s*=\s*'[^']*'/gi, "");
    return {
      inner: `<g fill="${fill}" stroke="none">${inner}</g>`,
      vx,
      vy,
      vw,
      vh,
    };
  }
  const key = (shape.preset === "custom" ? "heart" : shape.preset) as Exclude<
    ShapeMaskPreset,
    "custom"
  >;
  return {
    inner: `<path d="${PRESET_PATHS[key]}" fill="${fill}" />`,
    vx: 0,
    vy: 0,
    vw: 100,
    vh: 100,
  };
};

/**
 * Build an SVG sized exactly w × h CSS pixels. The shape (always treated as a
 * square unit in its own viewBox) is positioned, rotated, and scaled relative
 * to the container's pixel dimensions, so it never gets stretched or clipped
 * by CSS `mask-size` aspect math. Apply with `mask-size: 100% 100%`.
 *
 * `sizeOverride` lets callers (halo) reuse the same geometry with a different scale.
 */
export const buildShapeSvg = (
  shape: ShapeMaskConfig,
  whiteOnTransparent: boolean,
  w: number,
  h: number,
  sizeOverride?: number,
  colorOverride?: string,
): string => {
  const fill = colorOverride ?? (whiteOnTransparent ? "#fff" : "#000");
  const rot = shape.rotation || 0;
  const feather = Math.max(0, shape.feather || 0);
  const sizePct = Math.max(0.01, sizeOverride ?? shape.size);

  const { inner, vx, vy, vw, vh } = shapeBody(shape, fill);

  // Scale: sizePct% of min(w, h) defines the shape's bounding box edge length.
  const targetPx = (sizePct / 100) * Math.min(w, h);
  const unit = Math.min(vw, vh); // base shape edge in its own viewBox units
  const k = targetPx / unit;
  const tx = (shape.x / 100) * w;
  const ty = (shape.y / 100) * h;
  const cx = vx + vw / 2;
  const cy = vy + vh / 2;

  // Transform: move to shape center on canvas, rotate, scale, then re-center on the shape's own midpoint.
  const transform = `translate(${tx} ${ty}) rotate(${rot}) scale(${k}) translate(${-cx} ${-cy})`;

  const filter =
    feather > 0
      ? `<filter id="f" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${
          feather / 4
        }" /></filter>`
      : "";
  const filterAttr = feather > 0 ? ` filter="url(#f)"` : "";

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">${filter}<g transform="${transform}"${filterAttr}>${inner}</g></svg>`;
};


export const svgToDataUri = (svg: string): string =>
  `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

export const SHAPE_PRESET_OPTIONS: { value: ShapeMaskPreset; label: string }[] = [
  { value: "heart", label: "Heart" },
  { value: "circle", label: "Circle" },
  { value: "blob", label: "Soft Blob" },
  { value: "leaf", label: "Leaf" },
  { value: "diamond", label: "Diamond" },
  { value: "hexagon", label: "Hexagon" },
  { value: "arch", label: "Arch" },
  { value: "custom", label: "Custom SVG…" },
];

export const SHAPE_MODE_OPTIONS: { value: ShapeMaskMode; label: string; description: string }[] = [
  {
    value: "mask",
    label: "Mask mesh to shape",
    description: "Mesh renders only inside the shape, feathered edges.",
  },
  {
    value: "silhouette",
    label: "Shape silhouette on top",
    description: "Flat-color shape sits on top of the mesh.",
  },
  {
    value: "vignette",
    label: "Vignette outside shape",
    description: "Outside the shape darkens to focus the eye inward.",
  },
];

/** Maximum size for an uploaded custom SVG (inlined into the JSON config). */
export const MAX_CUSTOM_SVG_BYTES = 50_000;
